import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Form, Button, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { withRouter, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { apagarMesa, buscarOptionsMesa, buscarTabelaMesa, salvarNovaMesa, ativarDesativarTodos, inserirGrupoTodos, removerGrupoTodos, ativarUsuarioUnico, desativarUsuarioUnico, removerMesaUsuario } from '~/Services/Service';
import { default as Selector } from 'react-select';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { FaPrint, FaSearch, FaTrash, FaRegArrowAltCircleDown, FaRegArrowAltCircleUp, FaExclamation } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Formik, Form as FormikForm, Field } from 'formik';

const EmpreendimentoSelector = ({ options, value, onChange, loading, error, searchLoading, searchError, }) => {

    const selectedOption =
        options.find(opt => String(opt.value) === String(value)) || null;

    return (
        <div className="d-flex flex-wrap align-items-center">
            <div style={{ minWidth: '600px', maxWidth: '600px' }}>
                <Selector
                    inputId="selector-emp"
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    name="empreendimento"
                    isSearchable={true}
                    isClearable={true}
                    options={options}
                    value={selectedOption}
                    onChange={selected => {
                        onChange({ target: { value: selected ? selected.value : '' } });
                    }}
                    placeholder="Todos..."
                    isDisabled={loading || options.length === 0}
                    isLoading={loading || searchLoading || options.length === 0}
                />
            </div>
            {error && (
                <small className="text-danger ml-2" role="alert">
                    {error}
                </small>
            )}
            {searchError && (
                <small className="text-danger ml-2" role="alert">
                    {searchError}
                </small>
            )}
        </div>
    );
};

const Mesas = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const familiaParam = queryParams.get('familia') || '';

    const [selectedEmpreendimento, setSelectedEmpreendimento] = useState(familiaParam);
    const [empOptions, setEmpOptions] = useState([]);
    const [empLoading, setEmpLoading] = useState(false);
    const [empError, setEmpError] = useState(null);
    const [empBuscarLoading, setEmpBuscarLoading] = useState(false);
    const [empBuscarError, setEmpBuscarError] = useState(null);
    const [isCorretor, setIsCorretor] = useState(false);
    const [optionsMesa, setOptionsMesa] = useState([]);
    const [optionsMesaLoading, setOptionsMesaLoading] = useState(false);
    const [tabelaCorretores, setTabelaCorretores] = useState([]);
    const [tabelaCorretoresLoading, setTabelaCorretoresLoading] = useState(false);
    const [tabelaColaboradores, setTabelaColaboradores] = useState([]);
    const [tabelaColaboradoresLoading, setTabelaColaboradoresLoading] = useState(false);
    const [listaAtendentes, setListaAtendentes] = useState([]);
    const empreendimentos = useSelector(state => state.empreendimentos);
    const corretoresScrollRef = useRef(null);
    const colaboradoresScrollRef = useRef(null);

    const salvarPosicaoScroll = useCallback((ref) => {
        if (ref && ref.current) {
            return ref.current.scrollTop;
        }
        return 0;
    }, []);

    const restaurarPosicaoScroll = useCallback((ref, posicao) => {
        if (ref && ref.current) {
            ref.current.scrollTop = posicao;
        }
    }, []);

    const handleRemoverMesa = async (codigoUsuario, familia) => {
        try {
            await removerMesaUsuario(codigoUsuario, selectedEmpreendimento);
            toast.success(`Mesa removida com sucesso`);
            await fetchTabelasMesa(true);
        } catch (error) {
            console.error('Erro ao ativar:', error);
            const errorMessage = (error.response && error.response.data && error.response.data.Message) || (error.message) || 'Erro ao remover mesa';
            toast.error(errorMessage);
        }
    };

    const handleAtivarUnico = async (codigoUsuario, tipoAtendimento, codigoGrupo) => {
        try {
            await ativarUsuarioUnico(codigoUsuario, selectedEmpreendimento, tipoAtendimento, codigoGrupo);
            toast.success(`${tipoAtendimento === 1 ? 'Colaborador' : 'Corretor'} ativado com sucesso`);
            await fetchTabelasMesa(true);
        } catch (error) {
            console.error('Erro ao ativar:', error);
            const errorMessage = (error.response && error.response.data && error.response.data.Message) || (error.message) || 'Erro ao ativar usuário';
            toast.error(errorMessage);
        }
    };

    const handleDesativarUnico = async (codigoUsuario, tipoAtendimento) => {
        try {
            await desativarUsuarioUnico(codigoUsuario, selectedEmpreendimento, tipoAtendimento);
            toast.success(`${tipoAtendimento === 1 ? 'Colaborador' : 'Corretor'} desativado com sucesso`);
            await fetchTabelasMesa(true);
        } catch (error) {
            console.error('Erro ao desativar:', error);
            const errorMessage = (error.response && error.response.data && error.response.data.Message) || (error.message) || 'Erro ao desativar usuário';
            toast.error(errorMessage);
        }
    };

    const handleExcluir = async (codigo, familia, tipoAtendimento, codigoGrupo) => {
        try {
            await apagarMesa(codigo, familia, tipoAtendimento, codigoGrupo);
            toast.success(`Mesa ${codigo} excluída com sucesso`);
            await fetchTabelasMesa(true);
            await fetchOptionsMesa();
        } catch (error) {
            const errorMessage = (error.response && error.response.data && error.response.data.Message) || (error.message) || `Erro ao excluir a mesa ${codigo}`;
            toast.error(errorMessage);
        }
    };

    const handleInserirTodos = async () => {
        try {
            if (!selectedEmpreendimento) {
                toast.error('Selecione um empreendimento primeiro');
                return;
            }
            const tipoAtendimento = 1;
            await inserirGrupoTodos(selectedEmpreendimento, tipoAtendimento);
            toast.success('Grupo inserido para todos os usuários com sucesso');
            await fetchTabelasMesa(true);
        } catch (error) {
            console.error('Erro ao inserir grupo:', error);
            const errorMessage = (error.response && error.response.data && error.response.data.Message) || (error.message) || 'Erro ao inserir grupo para todos os usuários';
            toast.error(errorMessage);
        }
    };

    const handleRemoverTodos = async () => {
        try {
            if (!selectedEmpreendimento) {
                toast.error('Selecione um empreendimento primeiro');
                return;
            }
            const tipoAtendimento = 1;
            await removerGrupoTodos(selectedEmpreendimento, tipoAtendimento);
            toast.success('Grupo removido de todos os usuários com sucesso');
            await fetchTabelasMesa(true);
        } catch (error) {
            console.error('Erro ao remover grupo:', error);
            const errorMessage = (error.response && error.response.data && error.response.data.Message) || (error.message) || 'Erro ao remover grupo de todos os usuários';
            toast.error(errorMessage);
        }
    };

    const handleAtivarTodos = async (tipo) => {
        try{
            await ativarDesativarTodos(selectedEmpreendimento, tipo, 1);
            toast.success(`Todos os ${tipo === 1 ? 'colaboradores' : 'corretores'} ativados com sucesso`);
            await fetchTabelasMesa(true);
        } catch (error) {
            toast.error(`Erro ao ativar todos os ${tipo === 1 ? 'colaboradores' : 'corretores'}`);
        }
    };

    const handleDesativarTodos = async (tipo) => {
        try{
            await ativarDesativarTodos(selectedEmpreendimento, tipo, 2);
            toast.success(`Todos os ${tipo === 1 ? 'colaboradores' : 'corretores'} desativados com sucesso`);
            await fetchTabelasMesa(true);
        } catch (error) {
            toast.error(`Erro ao desativar todos os ${tipo === 1 ? 'colaboradores' : 'corretores'}`);
        }
    };

    const fetchTabelasMesa = async (preservarScroll = false) => {
        if (!selectedEmpreendimento) {
            setTabelaCorretores([]);
            setTabelaColaboradores([]);
            return;
        }

        // Salvar posições do scroll antes de atualizar
        var scrollCorretores = preservarScroll ? salvarPosicaoScroll(corretoresScrollRef) : 0;
        var scrollColaboradores = preservarScroll ? salvarPosicaoScroll(colaboradoresScrollRef) : 0;

        setTabelaCorretoresLoading(true);
        setTabelaColaboradoresLoading(true);

        try {
            const [corretoresResponse, colaboradoresResponse] = await Promise.all([
                buscarTabelaMesa(selectedEmpreendimento, 2),
                buscarTabelaMesa(selectedEmpreendimento, 1),
            ]);

            console.log('Corretores:', corretoresResponse);
            console.log('Colaboradores:', colaboradoresResponse);
            setTabelaCorretores(corretoresResponse || []);
            setTabelaColaboradores(colaboradoresResponse || []);

            // Restaurar posições do scroll após atualizar (com pequeno delay para garantir renderização)
            if (preservarScroll) {
                setTimeout(function() {
                    restaurarPosicaoScroll(corretoresScrollRef, scrollCorretores);
                    restaurarPosicaoScroll(colaboradoresScrollRef, scrollColaboradores);
                }, 50);
            }
        } catch (error) {
            toast.error('Erro ao buscar dados das tabelas');
            setTabelaCorretores([]);
            setTabelaColaboradores([]);
        } finally {
            setTabelaCorretoresLoading(false);
            setTabelaColaboradoresLoading(false);
        }
    };

    const fetchOptionsMesa = async () => {
        if (!selectedEmpreendimento) {
            setOptionsMesa([]);
            return;
        }
        setOptionsMesaLoading(true);
        try {
            const tipoAtendimento = isCorretor ? 2 : 1;
            const familia = selectedEmpreendimento;
            const response = await buscarOptionsMesa(familia, tipoAtendimento);
            const options = response.map(item => ({
                value: item.codigo,
                label: item.codigo + ' - ' + item.descricao,
            })) || [];
            setOptionsMesa(options);
        } catch (error) {
            toast.error('Erro ao buscar opções de mesa');
            setOptionsMesa([]);
        } finally {
            setOptionsMesaLoading(false);
        }
    };

    useEffect(() => {
        fetchOptionsMesa();
    }, [selectedEmpreendimento, isCorretor]);

    useEffect(() => {
        fetchTabelasMesa();
    }, [selectedEmpreendimento]);

    useEffect(() => {
        setEmpLoading(true);
        setEmpError(null);
        try {
            let origem = [];
            if (empreendimentos && Array.isArray(empreendimentos.sort)) {
                origem = empreendimentos.sort.map(item => ({
                    value: item.codigo,
                    label: item.codigo + ' - ' + item.descricao,
                }));
            }
            if (!origem || origem.length === 0) {
                setEmpOptions([]);
            } else {
                setEmpOptions(origem);
            }
        } catch (e) {
            setEmpError(e.message || 'Falha ao carregar empreendimentos');
        } finally {
            setEmpLoading(false);
        }
    }, [empreendimentos]);


    return (
        <div
            className="container-fluid bg-light p-3"
            style={{ minHeight: '100vh' }}
        >
            <div className="lotes-header py-3 mb-3">
                <div className="d-flex align-items-center justify-content-between px-3">
                    <h4 className="lotes-header__title mb-0">Mesas</h4>
                    <div className="d-flex align-items-center">
                        <div className="mr-2">
                            <EmpreendimentoSelector
                                options={empOptions}
                                value={selectedEmpreendimento}
                                onChange={e => {
                                    setSelectedEmpreendimento(e.target.value)
                                }}
                                loading={empLoading}
                                error={empError}
                                searchLoading={empBuscarLoading}
                                searchError={empBuscarError}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded my-2 shadow-sm border">
                <div className="px-3 py-2 rounded-top" style={{ backgroundColor: '#0747a6' }}>
                    <h6 className="mb-0 text-white">Cadastrar colaboradores para atendimento</h6>
                </div>
                <div className="p-3">
                    <Formik
                        initialValues={{
                            atendente: [],
                        }}
                        onSubmit={async (values, { resetForm }) => {
                            const listaFormatada = listaAtendentes.map(item => ({
                                usuario: item.value,
                                familia: selectedEmpreendimento,
                                tipo_atendente: isCorretor ? 2 : 1
                            }));

                            try {
                                await salvarNovaMesa(listaFormatada);
                                toast.success('Atendentes salvos com sucesso');
                                resetForm();
                                setListaAtendentes([]);
                                await fetchTabelasMesa(true);
                                await fetchOptionsMesa();
                            } catch (error) {
                                toast.error('Erro ao salvar atendentes');
                            }
                        }}
                    >
                        {({ isSubmitting, values, handleChange, handleSubmit, setFieldValue }) => (
                            <FormikForm>
                                <div className="d-flex flex-wrap align-items-center">

                                    <div className="mr-3 mb-2" style={{ minWidth: '500px' }}>
                                        <Form.Group className="mb-0">
                                            <Form.Label>{isCorretor ? 'Corretor' : 'Atendente'}</Form.Label>
                                            <Selector
                                                styles={customStyles}
                                                components={{ DropdownIndicator }}
                                                name="atendente"
                                                isSearchable={true}
                                                isClearable={true}
                                                isMulti={true}
                                                options={optionsMesa}
                                                value={
                                                    Array.isArray(values.atendente)
                                                        ? optionsMesa.filter(opt => values.atendente.includes(opt.value))
                                                        : []
                                                }
                                                onChange={(selected) => {
                                                    const listaAtualizada = selected ? selected : [];
                                                    setFieldValue(
                                                        'atendente',
                                                        listaAtualizada.map(opt => opt.value)
                                                    );
                                                    setListaAtendentes(listaAtualizada);
                                                }}
                                                placeholder="Selecione..."
                                                isLoading={optionsMesaLoading}
                                                isDisabled={!selectedEmpreendimento || optionsMesaLoading}
                                            />
                                        </Form.Group>
                                    </div>

                                    <div className="mr-3 mb-2 d-flex align-items-center" style={{ paddingTop: '28px' }}>
                                        <Form.Check
                                            type="switch"
                                            id="ecorretor"
                                            name="ecorretor"
                                            label="Colaborador é corretor"
                                            checked={isCorretor}
                                            onChange={(e) => setIsCorretor(e.target.checked)}
                                        />
                                    </div>

                                    <div className="ml-auto mb-2 " style={{ paddingTop: '28px', minWidth: '260px' }}>
                                        <Button
                                            type="submit"
                                            variant="success"
                                            className="w-100 mb-3 "
                                            title="Buscar"
                                        >{isSubmitting ? 'Salvando...' : 'Salvar'}
                                        </Button>
                                    </div>
                                </div>
                            </FormikForm>
                        )}
                    </Formik>
                </div>
            </div>

            <div className="row my-2">
                <div className="col-md-6">
                    <div className="bg-white rounded shadow-sm border h-100">
                        <div className="px-3 py-2 rounded-top" style={{ backgroundColor: '#0747a6' }}>
                            <h6 className="mb-0 text-white">Corretores</h6>
                        </div>
                        <div className="p-3">
                            <div ref={corretoresScrollRef} style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                <Table hover size="sm">
                                    <thead style={{ top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                                        <tr>
                                            <th>Usuário</th>
                                            <th style={{ width: '140px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tabelaCorretoresLoading ? (
                                            <tr>
                                                <td colSpan="2" className="text-center text-muted">
                                                    Carregando...
                                                </td>
                                            </tr>
                                        ) : tabelaCorretores.length > 0 ? (
                                            tabelaCorretores.map((corretor, index) => (
                                                <tr key={corretor.usuario || index}>
                                                    <td>{corretor.nome || corretor.usuario}</td>
                                                    <td className="text-center">
                                                        {corretor.status === false && (
                                                            <>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    title="Excluir"
                                                                    onClick={() => handleExcluir(corretor.codigo_usuario, selectedEmpreendimento, 2, corretor.codigo_grupo)}
                                                                >
                                                                    <FaTrash />
                                                                </Button>
                                                                &nbsp;
                                                            </>
                                                        )}
                                                        {corretor.status === false ? (
                                                            <Button
                                                                variant="outline-success"
                                                                size="sm"
                                                                title="Ativar"
                                                                onClick={() => handleAtivarUnico(corretor.codigo_usuario, 2, corretor.codigo_grupo)}
                                                            >
                                                                <FaRegArrowAltCircleUp/>
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="outline-warning"
                                                                size="sm"
                                                                title="Desativar"
                                                                onClick={() => handleDesativarUnico(corretor.codigo_usuario, 2)}
                                                            >
                                                                <FaRegArrowAltCircleDown/>
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="2" className="text-center text-muted">
                                                    Nenhum corretor encontrado
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="d-flex gap-2 mt-2 justify-content-end">
                                <OverlayTrigger placement="top" overlay={<Tooltip>Ativa todos os corretores para o empreendimento selecionado</Tooltip>}>
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={() => handleAtivarTodos(2)}
                                        className="mr-2"
                                    >
                                        Ativar todos
                                    </Button>
                                </OverlayTrigger>
                                <OverlayTrigger placement="top" overlay={<Tooltip>Desativa todos os corretores e finaliza atendimentos ativos</Tooltip>}>
                                    <Button
                                        variant="outline-warning"
                                        size="sm"
                                        onClick={() => handleDesativarTodos(2)}
                                    >
                                        Desativar todos
                                    </Button>
                                </OverlayTrigger>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="bg-white rounded shadow-sm border h-100">
                        <div className="px-3 py-2 rounded-top" style={{ backgroundColor: '#0747a6' }}>
                            <h6 className="mb-0 text-white">Colaboradores</h6>
                        </div>
                        <div className="p-3">
                            <div ref={colaboradoresScrollRef} style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                <Table hover size="sm">
                                    <thead style={{ top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                                        <tr>
                                            <th>Mesa</th>
                                            <th>Usuário</th>
                                            <th style={{ width: '140px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tabelaColaboradoresLoading ? (
                                            <tr>
                                                <td colSpan="3" className="text-center text-muted">
                                                    Carregando...
                                                </td>
                                            </tr>
                                        ) : tabelaColaboradores.length > 0 ? (
                                            tabelaColaboradores.map((colaborador, index) => (
                                                <tr key={colaborador.usuario || index}>
                                                    <td>{colaborador.numero || colaborador.descricao}</td>
                                                    <td>{colaborador.nome || colaborador.usuario}</td>
                                                    <td className="text-center">
                                                        {colaborador.status === false && (
                                                            <>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    title="Excluir"
                                                                    onClick={() => handleExcluir(colaborador.codigo_usuario, selectedEmpreendimento, 1, colaborador.codigo_grupo)}
                                                                >
                                                                    <FaTrash />
                                                                </Button>
                                                                &nbsp;
                                                            </>
                                                        )}
                                                        {colaborador.status === false ? (
                                                            <Button
                                                                variant="outline-success"
                                                                size="sm"
                                                                title="Ativar"
                                                                onClick={() => handleAtivarUnico(colaborador.codigo_usuario, 1, colaborador.codigo_grupo)}
                                                            >
                                                                <FaRegArrowAltCircleUp/>
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="outline-warning"
                                                                size="sm"
                                                                title="Desativar"
                                                                onClick={() => handleDesativarUnico(colaborador.codigo_usuario, 1)}
                                                            >
                                                                <FaRegArrowAltCircleDown/>
                                                            </Button>
                                                        )}
                                                        &nbsp;
                                                        <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                title="Remover Mesa"
                                                                onClick={() => handleRemoverMesa(colaborador.codigo_usuario, selectedEmpreendimento)}
                                                            >
                                                                <FaExclamation/>
                                                            </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="3" className="text-center text-muted">
                                                    Nenhum colaborador encontrado
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="d-flex gap-2 mt-2 justify-content-end">
                                <OverlayTrigger placement="top" overlay={<Tooltip>Insere o grupo de lançamento para todos os colaboradores do empreendimento</Tooltip>}>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={handleInserirTodos}
                                        className="mr-2"
                                    >
                                        Inserir Todos
                                    </Button>
                                </OverlayTrigger>
                                <OverlayTrigger placement="top" overlay={<Tooltip>Remove o grupo de lançamento de todos os colaboradores do empreendimento</Tooltip>}>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={handleRemoverTodos}
                                        className="mr-2"
                                    >
                                        Remover Todos
                                    </Button>
                                </OverlayTrigger>
                                <OverlayTrigger placement="top" overlay={<Tooltip>Ativa todos os colaboradores para o empreendimento selecionado</Tooltip>}>
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={() => handleAtivarTodos(1)}
                                        className="mr-2"
                                    >
                                        Ativar todos
                                    </Button>
                                </OverlayTrigger>
                                <OverlayTrigger placement="top" overlay={<Tooltip>Desativa todos os colaboradores e finaliza atendimentos ativos</Tooltip>}>
                                    <Button
                                        variant="outline-warning"
                                        size="sm"
                                        onClick={() => handleDesativarTodos(1)}
                                    >
                                        Desativar Todos
                                    </Button>
                                </OverlayTrigger>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default Mesas;
